export * from './client';
import { ZodiosError, formDataPlugin } from '@zodios/core';
import axios, { AxiosError } from 'axios';
import { dotToParanSubscript } from '@aka/vue-kit';
import { recursivelyBuildFormData, spoofMultipartMethodInterceptor } from './utils';

import { ValidationError as _ValidationError, createApiClient } from './client';
import { jwtServiceInterceptor } from './authInterceptor';

import type { ZodiosEndpointParameter } from '@zodios/core';
import type { ZodSchema, z } from 'zod';

export const ValidationError = _ValidationError.transform(data => {
    const errors = Object.entries(data.errors).reduce<Record<string, [string, ...string[]]>>((acc, [key, value]) => {
        const newKey = dotToParanSubscript(key);
        acc[newKey] = value.map(v => v.replace(key, newKey)) as [string, ...string[]];
        return acc;
    }, {});
    return { ...data, errors };
});

export const apiAxiosInstance = axios.create({
    baseURL: import.meta.env.VITE_API_URL,
});
apiAxiosInstance.interceptors.request.use(jwtServiceInterceptor);
apiAxiosInstance.interceptors.request.use(spoofMultipartMethodInterceptor);
apiAxiosInstance.interceptors.response.use(null, (error: AxiosError) => {
    if (error.response?.status === 422) {
        const validationErrors = ValidationError.safeParse(error.response.data);
        if (validationErrors.success) {
            error.response.data = validationErrors.data;
        }
    }
    return Promise.reject(error);
});

apiAxiosInstance.interceptors.request.use(v => {
    useMiscStore().ongoingNetworkCalls++;
    return v;
});

apiAxiosInstance.interceptors.response.use(
    v => {
        useMiscStore().ongoingNetworkCalls--;
        return v;
    },
    v => {
        useMiscStore().ongoingNetworkCalls--;
        return Promise.reject(v);
    },
);

const api = createApiClient(import.meta.env.VITE_API_URL, {
    axiosInstance: apiAxiosInstance,
});

for (const endpoint of api.api) {
    if (endpoint.requestFormat !== 'form-data') {
        continue;
    }

    api.use(endpoint.alias, {
        name: formDataPlugin().name,
        request: async (_, config) => {
            if (typeof config.data !== 'object' || config.data == null || Array.isArray(config.data)) {
                throw new ZodiosError('Zodios: multipart/form-data body must be an object', config.data as any);
            }
            const formData = new FormData();
            for (const [key, value] of Object.entries(config.data)) {
                recursivelyBuildFormData(value, formData, key);
            }
            return {
                ...config,
                data: formData,
            };
        },
    });
}

export { api };

export type Endpoint<P extends readonly ZodiosEndpointParameter[], Z extends ZodSchema> = {
    alias: string;
    parameters?: P;
    response: Z;
};

export type OmitNever<T extends Record<string, any>> = {
    [key in keyof T as T[key] extends never ? never : key]: T[key];
};

export type ExtractParametersOfType<
    Type extends 'Path' | 'Query' | 'Body',
    P extends readonly ZodiosEndpointParameter[],
> = OmitNever<{
    [key in P[number]['name']]: z.infer<
        Extract<
            P[number],
            {
                name: key;
                type: Type;
            }
        >['schema']
    >;
}>;
